import type { RegistryModel } from '../hooks/useModels';

/** Session-level model badge (short label + CSS class + avatar color + icon path) */
export function modelBadge(
  model: string | null,
  isRelay: boolean,
): { label: string; cls: string; color: string; icon: string } {
  if (isRelay) return { label: 'SR', cls: 'relay', color: '#4ecca3', icon: '/avatars/relay.svg' };
  if (!model || model === 'cascade') return { label: 'CA', cls: 'cascade', color: '#4ec9b0', icon: '/avatars/default.svg' };
  if (model.includes('opus')) return { label: 'O', cls: 'opus', color: '#bc8cff', icon: '/avatars/claude-opus.svg' };
  if (model.includes('sonnet')) return { label: 'S', cls: 'sonnet', color: '#56a0d4', icon: '/avatars/claude-sonnet.svg' };
  if (model.includes('haiku')) return { label: 'H', cls: 'haiku', color: '#8888aa', icon: '/avatars/claude-haiku.svg' };
  if (model.includes('gemini') && (model.includes('flash') || model.includes('lite'))) return { label: 'GF', cls: 'gemini', color: '#22d3ee', icon: '/avatars/gemini-flash.svg' };
  if (model.includes('gemini')) return { label: 'G', cls: 'gemini', color: '#34a853', icon: '/avatars/gemini.svg' };
  if (model.includes('gpt') || model.includes('o3') || model.includes('o4')) return { label: 'AI', cls: 'openai', color: '#74aa9c', icon: '/avatars/openai.svg' };
  if (model.includes('qwen')) return { label: 'Q', cls: 'qwen', color: '#7a5cff', icon: '/avatars/qwen.svg' };
  return { label: '?', cls: '', color: '#666', icon: '/avatars/default.svg' };
}

/** Human-friendly short model name for display in session rows */
export function modelShortName(model: string | null, isRelay: boolean): string {
  if (isRelay) return 'Relay';
  if (!model) return 'Claude';
  if (model === 'cascade') return 'Cascade Agent';
  if (model.includes('opus')) {
    const v = model.match(/(\d+[\w.]+)/)?.[1];
    return v ? `Opus ${v}` : 'Opus';
  }
  if (model.includes('sonnet')) {
    const v = model.match(/(\d+[\w.]+)/)?.[1];
    return v ? `Sonnet ${v}` : 'Sonnet';
  }
  if (model.includes('haiku')) {
    const v = model.match(/(\d+[\w.]+)/)?.[1];
    return v ? `Haiku ${v}` : 'Haiku';
  }
  if (model.includes('gemini') && model.includes('flash-lite')) return 'Gemini Flash Lite';
  if (model.includes('gemini') && model.includes('flash')) return 'Gemini Flash';
  if (model.includes('gemini')) {
    const v = model.match(/gemini[- ]?(\S+)/i)?.[1];
    return v ? `Gemini ${v}` : 'Gemini';
  }
  if (model.includes('gpt-4o')) return 'GPT-4o';
  if (model.includes('gpt')) return 'GPT';
  if (model.includes('o3')) return 'o3';
  if (model.includes('o4')) return 'o4';
  if (model.includes('qwen')) {
    const v = model.match(/qwen[- ]?(\S+)/i)?.[1];
    return v ? `Qwen ${v}` : 'Qwen';
  }
  return model;
}

/**
 * Compact per-turn model label (model-meta-hardening Phase 3/4) — registry = single source of truth.
 *
 * Looks the raw model id up in the `useModels()` registry cache; on a HIT it renders a compact
 * label (e.g. "Sonnet 4.6", "Gemini Flash", "Cascade"). On a MISS (unknown / newly-rolled-out id,
 * or empty cache during first load) it falls back to the id-based `modelShortName` parser so the
 * badge degrades gracefully instead of crashing or showing a raw model string.
 *
 * Why not just the registry `displayName`? It is verbose ("Claude Sonnet 4.6", "Cascade Agent").
 * The compact id-based parser already yields the product's intended badge form ("Sonnet 4.6",
 * "Cascade") and handles cascade's "Cascade Agent" → "Cascade" trim. We confirm identity via the
 * registry, then render compactly.
 */
export function turnModelLabel(model: string | null, models: RegistryModel[]): string {
  if (!model) return modelShortName(null, false);
  // Cascade is the synthetic agent — keep the badge tight ("Cascade", not "Cascade Agent").
  if (model === 'cascade') return 'Cascade';
  const hit = models.find((m) => m.modelId === model);
  if (hit) {
    // Registry = single source of truth: its `displayName` carries the clean, versioned label
    // ("Claude Sonnet 4.6", "Gemini 2.5 Flash"). Strip the redundant "Claude " prefix and collapse
    // the verbose "Gemini 2.5 Flash" to the product's compact "Gemini Flash" form. The dash-form id
    // parser (modelShortName) can't reliably recover the version from "claude-sonnet-4-6", which is
    // exactly why the registry is authoritative here.
    const name = (hit.displayName ?? hit.modelId).replace(/^claude\s+/i, '');
    if (hit.provider === 'google') {
      // "Gemini 2.5 Flash" → "Gemini Flash"; "Gemini 2.5 Flash Lite" → "Gemini Flash Lite".
      const variant = name.replace(/^gemini\s+[\d.]+\s*/i, '').trim();
      return variant ? `Gemini ${variant}` : name;
    }
    return name;
  }
  // Registry miss — graceful fallback to the id parser (best-effort, never crashes).
  return modelShortName(model, false);
}

/** Map registry tier → CSS class for new-session buttons */
export function tierCssClass(model: RegistryModel): string {
  if (model.provider === 'anthropic') {
    if (model.tier === 'powerful') return 'opus';
    if (model.tier === 'balanced') return 'sonnet';
    return 'haiku';
  }
  if (model.tier === 'fast') return 'gemini-flash';
  return 'gemini';
}

/** Format token count as "1M", "200K", etc. */
export function fmtTokens(n: number | null): string | null {
  if (!n) return null;
  if (n >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

/**
 * Extract a short label + optional version tag from a model's displayName.
 * "Claude Sonnet 4.6" → { label: "Sonnet", tag: "4.6" }
 * "Gemini 2.5 Pro"   → { label: "2.5 Pro", tag: null }
 */
export function shortLabel(model: RegistryModel): { label: string; tag: string | null } {
  const name = model.displayName ?? model.modelId;
  if (model.provider === 'anthropic') {
    const stripped = name.replace(/^claude\s+/i, '');
    const versionMatch = stripped.match(/^(.+?)\s+(\d+[\w.]+)$/i);
    if (versionMatch) return { label: versionMatch[1], tag: versionMatch[2] };
    return { label: stripped, tag: null };
  }
  if (model.provider === 'google') {
    return { label: name.replace(/^gemini\s+/i, ''), tag: null };
  }
  return { label: name, tag: null };
}

/** Relative time formatter */
export function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}
